// Trading fees on Polymarket and Kalshi, from each venue's published schedule
// (read 21 October 2026):
// - Polymarket, docs.polymarket.com/trading/fees: fee = C × feeRate × p × (1 − p),
//   charged to takers only, rounded to 5 decimals; the rate depends on the
//   market's category.
// - Kalshi, "Fee Schedule for July 2026": fees = round up(M × 0.07 × C × P × (1 − P))
//   for takers, 0.0175 instead of 0.07 for makers on markets with maker fees,
//   rounded up to a centicent (0.0001 dollars).
// Prices are dollars per contract (0.48 = 48¢); one contract pays $1 if it wins.

export const POLYMARKET_CATEGORY_RATES = {
  sports: 0.05,
  crypto: 0.07,
  finance: 0.04,
  politics: 0.04,
  economics: 0.05,
  culture: 0.05,
  weather: 0.05,
  mentions: 0.04,
  tech: 0.04,
  geopolitics: 0,
  combos: 0.05,
  other: 0.05,
} as const;

export type PolymarketCategory = keyof typeof POLYMARKET_CATEGORY_RATES;
export const POLYMARKET_CATEGORIES = Object.keys(
  POLYMARKET_CATEGORY_RATES,
) as PolymarketCategory[];

export const KALSHI_TAKER_RATE = 0.07;
export const KALSHI_MAKER_RATE = 0.0175;

export type FeeVenue = "polymarket" | "kalshi";

export type FeeInput = {
  venue: FeeVenue;
  // Dollars per contract, strictly between 0 and 1.
  price: number;
  contracts: number;
  maker?: boolean;
  // Polymarket market category (default sports).
  category?: PolymarketCategory;
  // Kalshi contract multiplier M (default 1).
  multiplier?: number;
};

export type FeeQuote = {
  venue: FeeVenue;
  price: number;
  contracts: number;
  // Total fee in dollars for the order.
  fee: number;
  feePerContract: number;
  // Fee as a share of what the contracts cost, in percent.
  feePercent: number;
  // Contracts × price, before the fee.
  cost: number;
  totalCost: number;
  // The win probability the position needs to break even: total cost per contract.
  breakEven: number;
  // Decimal odds after the fee: what each dollar staked returns if it wins.
  netOdds: number;
  payout: number;
  profitIfWin: number;
};

const roundTo = (value: number, places: number) => {
  const factor = 10 ** places;
  return Math.round(value * factor) / factor;
};

// Rounded up to the next centicent, ignoring floating-point dust.
const roundUpCenticent = (value: number) =>
  Math.ceil(roundTo(value * 10_000, 6)) / 10_000;

const validPrice = (price: number) =>
  Number.isFinite(price) && price > 0 && price < 1;
const validContracts = (contracts: number) =>
  Number.isFinite(contracts) && contracts > 0;

export function polymarketFee(
  price: number,
  contracts: number,
  category: PolymarketCategory = "sports",
  maker = false,
) {
  if (maker) return 0;
  const rate = POLYMARKET_CATEGORY_RATES[category];
  return roundTo(contracts * rate * price * (1 - price), 5);
}

export function kalshiFee(
  price: number,
  contracts: number,
  maker = false,
  multiplier = 1,
) {
  const rate = maker ? KALSHI_MAKER_RATE : KALSHI_TAKER_RATE;
  return roundUpCenticent(multiplier * rate * contracts * price * (1 - price));
}

export function feeQuote(input: FeeInput): FeeQuote | null {
  const { venue, price, contracts } = input;
  if (!validPrice(price) || !validContracts(contracts)) return null;
  const fee =
    venue === "polymarket"
      ? polymarketFee(price, contracts, input.category ?? "sports", input.maker)
      : kalshiFee(price, contracts, input.maker, input.multiplier ?? 1);
  const cost = contracts * price;
  const totalCost = cost + fee;
  return {
    venue,
    price,
    contracts,
    fee,
    feePerContract: fee / contracts,
    feePercent: (fee / cost) * 100,
    cost,
    totalCost,
    breakEven: totalCost / contracts,
    netOdds: contracts / totalCost,
    payout: contracts,
    profitIfWin: contracts - totalCost,
  };
}

export type CrossVenueArb = {
  polymarket: FeeQuote;
  kalshi: FeeQuote;
  totalCost: number;
  // Whichever outcome wins, the matching contracts pay $1 each.
  payout: number;
  profit: number;
  // Profit as a share of the total cost, in percent.
  returnPercent: number;
  isArb: boolean;
};

// Buying one outcome on Polymarket and the opposite outcome on Kalshi, the
// same number of contracts on each side.
export function crossVenueArb(input: {
  polymarketPrice: number;
  kalshiPrice: number;
  contracts: number;
  category?: PolymarketCategory;
  polymarketMaker?: boolean;
  kalshiMaker?: boolean;
}): CrossVenueArb | null {
  const polymarket = feeQuote({
    venue: "polymarket",
    price: input.polymarketPrice,
    contracts: input.contracts,
    category: input.category,
    maker: input.polymarketMaker,
  });
  const kalshi = feeQuote({
    venue: "kalshi",
    price: input.kalshiPrice,
    contracts: input.contracts,
    maker: input.kalshiMaker,
  });
  if (!polymarket || !kalshi) return null;
  const totalCost = polymarket.totalCost + kalshi.totalCost;
  const payout = input.contracts;
  const profit = payout - totalCost;
  return {
    polymarket,
    kalshi,
    totalCost,
    payout,
    profit,
    returnPercent: (profit / totalCost) * 100,
    isArb: profit > 0,
  };
}
